import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Upload,
  Trash2,
  LogOut,
  ImagePlus,
  Paperclip,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  "Logos + Brand Marks",
  "Digital Marketing",
  "Print + Displays",
  "Email + Publications",
  "Custom Creative",
  "Portal & Workflow Design",
];
const inputClass =
  "bg-[#120B24] border-[#2D1B4E] text-white placeholder:text-[#6d5b95] focus-visible:ring-[#8A2BE2] focus-visible:border-[#8A2BE2] h-12 rounded-xl";

export default function Admin() {
  const [token, setToken] = useState(
    () => localStorage.getItem("cmd_admin_token") || ""
  );
  const [authed, setAuthed] = useState(false);
  const [tokenInput, setTokenInput] = useState("");

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [items, setItems] = useState([]);
  const [messages, setMessages] = useState([]);

  const authHeader = { headers: { "X-Admin-Token": token } };

  const loadData = useCallback(async () => {
    try {
      const [w, c] = await Promise.all([
        axios.get(`${API}/work`),
        axios.get(`${API}/contact`, { headers: { "X-Admin-Token": token } }),
      ]);
      setItems(w.data || []);
      setMessages(c.data || []);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        toast.error("Invalid admin token.");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) loadData();
  }, [token, loadData]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    localStorage.setItem("cmd_admin_token", tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const logout = () => {
    localStorage.removeItem("cmd_admin_token");
    setToken("");
    setAuthed(false);
  };

  const addWork = async (e) => {
    e.preventDefault();
    if (!title.trim() || !category || !file) {
      toast.error("Add a title, category and image.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/upload`, fd);
      await axios.post(
        `${API}/work`,
        { title, category, image_path: up.data.path },
        authHeader
      );
      toast.success("Project added.");
      setTitle("");
      setCategory("");
      setFile(null);
      loadData();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to add project.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const removeWork = async (id) => {
    try {
      await axios.delete(`${API}/work/${id}`, authHeader);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#090514] grid place-items-center px-6">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-[#2D1B4E] bg-[#0b0618] p-8"
        >
          <div className="flex items-center mb-2">
            <span className="font-display font-extrabold text-xl text-white">
              CMD<span className="text-[#C77DFF]">+</span>DesignLab
              <sup className="align-super text-[0.5em] text-[#C77DFF] ml-0.5">
                &reg;
              </sup>
            </span>
          </div>
          <p className="text-[#B5A6CC] text-sm mb-6">Studio admin access</p>
          <Label className="text-[#B5A6CC]">Admin token</Label>
          <Input
            type="password"
            data-testid="admin-token-input"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Enter admin token"
            className={`${inputClass} mt-2`}
          />
          <Button
            type="submit"
            data-testid="admin-login-button"
            className="mt-6 w-full rounded-full bg-[#8A2BE2] hover:bg-[#9D4EDD] text-white py-6"
          >
            Enter Studio
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090514] text-white">
      <header className="border-b border-[#2D1B4E] px-6 md:px-12 h-20 flex items-center justify-between">
        <div className="flex items-center">
          <span className="font-display font-extrabold text-xl">
            CMD<span className="text-[#C77DFF]">+</span>DesignLab
            <sup className="align-super text-[0.5em] text-[#C77DFF] ml-0.5">
              &reg;
            </sup>
          </span>
          <span className="ml-3 text-xs uppercase tracking-[0.3em] text-[#6d5b95]">
            Admin
          </span>
        </div>
        <Button
          onClick={logout}
          data-testid="admin-logout-button"
          variant="ghost"
          className="text-[#B5A6CC] hover:text-white"
        >
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </Button>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 md:px-12 py-12 grid lg:grid-cols-12 gap-10">
        {/* Upload */}
        <section className="lg:col-span-5">
          <h2 className="font-display font-bold text-2xl mb-6 flex items-center gap-2">
            <ImagePlus className="h-5 w-5 text-[#C77DFF]" /> Add a project
          </h2>
          <form
            onSubmit={addWork}
            data-testid="admin-work-form"
            className="rounded-3xl border border-[#2D1B4E] bg-[#0b0618] p-6 space-y-5"
          >
            <div className="space-y-2">
              <Label className="text-[#B5A6CC]">Title</Label>
              <Input
                data-testid="admin-work-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Project name"
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#B5A6CC]">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  data-testid="admin-work-category"
                  className={inputClass}
                >
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-[#120B24] border-[#2D1B4E] text-white">
                  {CATEGORIES.map((c) => (
                    <SelectItem
                      key={c}
                      value={c}
                      className="focus:bg-[#8A2BE2] focus:text-white"
                    >
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#B5A6CC]">Image</Label>
              <label
                htmlFor="admin-file"
                className="flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-[#3a2166] hover:border-[#8A2BE2] bg-[#120B24] px-4 py-4 transition-colors"
              >
                <Paperclip className="h-5 w-5 text-[#C77DFF]" />
                <span className="text-sm text-[#B5A6CC] truncate">
                  {file ? file.name : "Choose an image (max 10MB)"}
                </span>
                <input
                  id="admin-file"
                  data-testid="admin-work-file"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>
            <Button
              type="submit"
              data-testid="admin-work-submit"
              disabled={busy}
              className="w-full rounded-full bg-[#8A2BE2] hover:bg-[#9D4EDD] text-white py-6"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" /> Publish Project
                </>
              )}
            </Button>
          </form>
        </section>

        {/* Existing work */}
        <section className="lg:col-span-7">
          <h2 className="font-display font-bold text-2xl mb-6">
            Published projects ({items.length})
          </h2>
          {items.length === 0 ? (
            <p className="text-[#6d5b95]">No projects yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-work-list">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="relative overflow-hidden rounded-2xl border border-[#2D1B4E] bg-[#0b0618]"
                >
                  <img
                    src={`${API}/files/${it.image_path}`}
                    alt={it.title}
                    className="h-40 w-full object-cover"
                  />
                  <div className="p-4">
                    <span className="text-xs uppercase tracking-widest text-[#C77DFF]">
                      {it.category}
                    </span>
                    <div className="font-display font-bold">{it.title}</div>
                  </div>
                  <button
                    data-testid={`admin-delete-${it.id}`}
                    onClick={() => removeWork(it.id)}
                    className="absolute top-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Contact submissions */}
          <h2 className="font-display font-bold text-2xl mt-12 mb-6">
            Project inquiries ({messages.length})
          </h2>
          <div className="space-y-4" data-testid="admin-messages">
            {messages.length === 0 && (
              <p className="text-[#6d5b95]">No inquiries yet.</p>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className="rounded-2xl border border-[#2D1B4E] bg-[#0b0618] p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold">{m.name}</span>
                  <a
                    href={`mailto:${m.email}`}
                    className="text-sm text-[#C77DFF]"
                  >
                    {m.email}
                  </a>
                </div>
                <div className="mt-1 text-xs text-[#6d5b95]">
                  {[m.project_type, m.budget].filter(Boolean).join(" · ")}
                </div>
                <p className="mt-3 text-sm text-[#B5A6CC]">{m.message}</p>
                {m.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {m.attachments.map((a) => (
                      <a
                        key={a.path}
                        href={`${API}/files/${a.path}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 rounded-full border border-[#2D1B4E] px-3 py-1.5 text-xs text-white hover:border-[#8A2BE2]"
                      >
                        <Paperclip className="h-3 w-3 text-[#C77DFF]" />
                        {a.filename}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
